import React, { useState } from 'react'

import {
  Box,
  Button,
  Chip,
  LinearProgress,
  Link,
  MenuItem,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import {
  CATEGORY_LABELS,
  costLabel,
  itemLabel,
  itemOriginLabel,
  METER_LABELS,
  type ShowFilter,
} from 'features/customers/constants/contactsActivity'
import {
  useCustomerCreditRequests,
  useCustomerDeliveredPeople,
} from 'features/customers/hooks/useCustomerContactsActivity'

import { Contacts } from '@albacross/types'

import Table from 'components/table/Table'
import type { ColumnConfig } from 'components/table/types'

import type { ReservationLink } from './ContactsUnlocks'
import { formatUtc } from './formatUtc'
import { describeItem, noJobNote } from './requestOutcome'
import { searchErrorOf } from './searchError'
import { useClearWhenLinked } from './useClearWhenLinked'
import { useDebouncedSearch } from './useDebouncedSearch'
import { usePageOf } from './usePageOf'

const ALL = 'all'

const seconds = (job: Contacts.ProviderJobSummary) =>
  job.finishedAt ? `${Math.round((Date.parse(job.finishedAt) - Date.parse(job.startedAt)) / 1000)} s` : 'still running'

const totalsLabel = (totals: Contacts.MeterTotals | null) => {
  if (!totals) return '–'
  return totals.free ? `${totals.charged} + ${totals.free} free` : String(totals.charged)
}

const leadOf = ({ origin }: Contacts.CreditRequestItem) =>
  origin.kind === 'workflow' || origin.kind === 'manual_send' ? origin.leadUuid : null

const COLUMNS: ColumnConfig<Contacts.CreditRequestItem>[] = [
  {
    title: 'Status',
    render: item => {
      const { chip, tone } = describeItem(item, Date.now())
      return <Chip color={tone} label={chip} size="small" />
    },
  },
  {
    title: 'What',
    render: item => (
      <>
        {itemLabel(item)}
        {leadOf(item) && (
          <Typography color="text.secondary" component="div" variant="caption">
            Lead {leadOf(item)}
          </Typography>
        )}
      </>
    ),
  },
  { title: 'Outcome', render: item => describeItem(item, Date.now()).sentence },
  { title: METER_LABELS.email, noBreak: true, render: item => totalsLabel(item.totals.email) },
  { title: METER_LABELS.phone, noBreak: true, render: item => totalsLabel(item.totals.phone) },
  { title: 'When (UTC)', noBreak: true, render: item => formatUtc(item.createdAt) },
]

const Detail = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <>
    <Typography color="text.secondary" component="dt" variant="body2">
      {label}
    </Typography>
    <Typography component="dd" sx={{ m: 0, wordBreak: 'break-all' }} variant="body2">
      {children ?? ''}
    </Typography>
  </>
)

type OpenRequest = (reservationId: number) => void

type ShowPeople = (request: Contacts.CreditRequest) => void

const TechnicalDetails = ({ request, onShowPeople }: { request: Contacts.CreditRequest; onShowPeople: ShowPeople }) => {
  const { job } = request
  return (
    <section aria-label="Technical details">
      <Typography component="h4" variant="subtitle2">
        Technical details
      </Typography>
      <Box
        component="dl"
        data-testid={`contacts-request-detail-${request.id}`}
        sx={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 2, rowGap: 0.5, m: 0 }}>
        <Detail label="Reservation">#{request.id}</Detail>
        <Detail label="State">{request.state}</Detail>
        <Detail label="Reason">{[request.reason, request.late && 'late_confirm'].filter(Boolean).join(', ')}</Detail>
        <Detail label="Asked">{request.requested}</Detail>
        <Detail label="Reserved">{request.granted}</Detail>
        <Detail label="People the provider returned">{request.returned}</Detail>
        <Detail label="Delivered">{request.delivered}</Detail>
        {!!request.charged && (
          <Detail label="Charged people">
            <Link component="button" onClick={() => onShowPeople(request)} variant="body2">
              People
            </Link>
          </Detail>
        )}
        <Detail label="Reference">{request.ref}</Detail>
        <Detail label="Trace id">{request.traceId}</Detail>
        <Detail label="Confirmed">{request.confirmedAt && formatUtc(request.confirmedAt)}</Detail>
        <Detail label="Reserved until">{formatUtc(request.expiresAt)}</Detail>
        {job ? (
          <>
            <Detail label="Provider job">
              {job.provider} {job.kind} #{job.id}: {job.status}
              {job.providerStatus ? ` (${job.providerStatus})` : ''}
            </Detail>
            <Detail label="People found">{job.peopleFound}</Detail>
            <Detail label="Our cost">{costLabel(job)}</Detail>
            <Detail label="Took">{seconds(job)}</Detail>
            {job.lastError && <Detail label="Error">{job.lastError}</Detail>}
          </>
        ) : (
          <Detail label="Provider job">{noJobNote(request)}</Detail>
        )}
      </Box>
    </section>
  )
}

const firstChargeText = ({ at, origin }: Contacts.FirstCharge) =>
  `Free: first charged ${formatUtc(at)} by ${origin ? itemOriginLabel(origin) : 'the old path, before the ledger'}`

const PersonCharge = ({ person, onOpenRequest }: { person: Contacts.DeliveredPerson; onOpenRequest: OpenRequest }) => {
  const { charged, firstCharge } = person
  if (charged) return 'Charged'
  if (!firstCharge) return 'Free: already charged this month'
  const { reservationId } = firstCharge
  return reservationId ? (
    <Link component="button" onClick={() => onOpenRequest(reservationId)} variant="body2">
      {firstChargeText(firstCharge)}
    </Link>
  ) : (
    firstChargeText(firstCharge)
  )
}

const People = ({
  item,
  period,
  onOpenRequest,
}: {
  item: Contacts.CreditRequestItem
  period: string
  onOpenRequest: OpenRequest
}) => {
  const { data, error } = useCustomerDeliveredPeople(period, item.key)
  const several = item.domains.length > 1
  const body = () => {
    if (error) return <Typography variant="body2">Could not load the people.</Typography>
    if (!data) return <Typography variant="body2">Loading people…</Typography>
    if (!data.rows.length) {
      return (
        <Typography variant="body2">
          No people recorded: nothing was delivered, or it was delivered before the ledger kept who.
        </Typography>
      )
    }
    return (
      <>
        <MuiTable aria-label="People" size="small">
          <TableHead>
            <TableRow>
              {several && <TableCell>Company</TableCell>}
              <TableCell>Person</TableCell>
              <TableCell>Credits</TableCell>
              <TableCell>Charged</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.rows.map(person => (
              <TableRow key={`${person.reservationId}:${person.meter}:${person.personKey}`}>
                {several && <TableCell>{person.domain}</TableCell>}
                <TableCell sx={{ wordBreak: 'break-all' }}>{person.personKey}</TableCell>
                <TableCell>{METER_LABELS[person.meter]}</TableCell>
                <TableCell>
                  <PersonCharge onOpenRequest={onOpenRequest} person={person} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </MuiTable>
        {data.total > data.rows.length && (
          <Typography color="text.secondary" variant="caption">
            The first {data.rows.length} of {data.total} people.
          </Typography>
        )}
      </>
    )
  }
  return (
    <section aria-label="People">
      <Typography component="h4" variant="subtitle2">
        People
      </Typography>
      {body()}
    </section>
  )
}

const ItemDetails = ({
  item,
  period,
  onOpenRequest,
  onShowPeople,
}: {
  item: Contacts.CreditRequestItem
  period: string
  onOpenRequest: OpenRequest
  onShowPeople: ShowPeople
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <People item={item} onOpenRequest={onOpenRequest} period={period} />
    {item.requests.map(request => (
      <TechnicalDetails key={request.id} onShowPeople={onShowPeople} request={request} />
    ))}
  </Box>
)

type ContactsRequestsProps = {
  period: string
  show: ShowFilter
  onShow: (show: ShowFilter) => void
  reservation: ReservationLink | null
  onClearReservation: () => void
  onShowRequest: OpenRequest
  onShowPeople: ShowPeople
}

const ContactsRequests = ({
  period,
  show,
  onShow,
  reservation,
  onClearReservation,
  onShowRequest,
  onShowPeople,
}: ContactsRequestsProps) => {
  const reservationId = reservation?.reservationId
  const { text, setText, search, clear } = useDebouncedSearch()
  const [advanced, setAdvanced] = useState(false)
  const [outcome, setOutcome] = useState<Contacts.RequestOutcome | typeof ALL>(ALL)
  const [pageSize, setPageSize] = useState(25)

  useClearWhenLinked(reservation, () => {
    clear()
    setOutcome(ALL)
  })

  const { page, setPage } = usePageOf(JSON.stringify([period, search, reservationId, show, outcome, pageSize]))

  const { data, isFetching, error } = useCustomerCreditRequests({
    period,
    ...(search ? { q: search } : {}),
    ...(reservationId ? { reservation_id: reservationId } : {}),
    ...(show === ALL ? {} : { show }),
    ...(outcome === ALL ? {} : { outcome }),
    page: page + 1,
    page_size: pageSize,
  })

  const toolbar = (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      <TextField
        helperText="Exact: export:812, wf:7, lead:6f1c, trace:…"
        label="Search"
        onChange={e => setText(e.target.value)}
        placeholder="Domain, export, workflow, lead, user or trace id"
        size="small"
        sx={{ minWidth: 360 }}
        value={text}
      />
      {reservation && (
        <Chip label={`Request #${reservationId}`} onDelete={onClearReservation} sx={{ alignSelf: 'center' }} />
      )}
      <TextField
        label="Show"
        onChange={e => onShow(e.target.value as ShowFilter)}
        select
        size="small"
        sx={{ minWidth: 200 }}
        value={show}>
        <MenuItem value={ALL}>All</MenuItem>
        {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
          <MenuItem key={value} value={value}>
            {label}
          </MenuItem>
        ))}
      </TextField>
      <Button aria-expanded={advanced} onClick={() => setAdvanced(!advanced)} size="small">
        Advanced
      </Button>
      {advanced && (
        <TextField
          label="Ledger reason"
          onChange={e => setOutcome(e.target.value as Contacts.RequestOutcome | typeof ALL)}
          select
          size="small"
          sx={{ minWidth: 200 }}
          value={outcome}>
          <MenuItem value={ALL}>Any</MenuItem>
          {Contacts.REQUEST_OUTCOMES.map(value => (
            <MenuItem key={value} value={value}>
              {value}
            </MenuItem>
          ))}
        </TextField>
      )}
    </Box>
  )

  return (
    <section aria-label="Requests" data-testid="contacts-requests">
      <LinearProgress aria-label="Loading requests" style={{ visibility: isFetching ? 'visible' : 'hidden' }} />
      <Table<Contacts.CreditRequestItem>
        config={COLUMNS}
        count={data?.total}
        data={data?.rows ?? []}
        error={error ? searchErrorOf(error, 'Could not load the requests') : undefined}
        loading={isFetching && !data}
        minWidth={900}
        onChangePage={setPage}
        onChangeRowsPerPage={value => setPageSize(Number(value))}
        page={page}
        renderDetailView={(item: Contacts.CreditRequestItem) => (
          <ItemDetails item={item} onOpenRequest={onShowRequest} onShowPeople={onShowPeople} period={period} />
        )}
        rowsPerPage={pageSize}
        size="small"
        title="Requests"
        toolbar={toolbar}
      />
    </section>
  )
}

export default ContactsRequests
